/* RabbitVis BiBu companion for the hosted Logto sign-in pages (UI handoff 2026-10-15).
   Ported from rabbitvis-frontend docs/backend-handoff/login-ui-20260924/preview.html
   (ui/design-updates-20261012 @ 144d7233): click greetings, a curious hover, idle sleepiness
   and eyes that follow the mouse. The RabbitVis Logto theme draws every state from the
   data-bibu-* attributes and --gaze-* properties set here, and opts in with
   --rv-bibu-companion: on; without it this script does nothing.
   It reads only pointer positions and focus/validation state, never input values, and
   changes nothing in the authentication flow.
   Unlike the preview, gaze undoes BiBu's CSS rotate/scale and aims at the visor, so the
   mirrored artwork looks towards the pointer, and a mouse resting on BiBu keeps her curious
   (only the visor itself delights her) instead of being covered by the following gaze. */
(function () {
  'use strict';

  var REACTIONS = ['greeting', 'wink', 'surprised', 'love', 'shy'];
  var PROTECTED = 'input[type="password"]:focus,input[autocomplete="one-time-code"]:focus,'
    + 'input[autocomplete="one-time-code"][type="text"],input[autocomplete="current-password"][type="text"],'
    + 'input[autocomplete="new-password"][type="text"],[class*="passcode"] input:focus';
  var INVALID = 'input[aria-invalid="true"],[role="alert"][class*="error"]:not(:empty)';
  // Visor centre in the 2048x2560 artwork (bibu-gaze-open.svg) and the preview's 700px
  // reference width for gaze distances.
  var EYE_X = 1009 / 2048;
  var EYE_Y = 1184 / 2560;
  var REFERENCE_WIDTH = 700;

  var motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var stage = null;
  var trigger = null;
  var greetingTimer = 0;
  var idleTimer = 0;
  var greetingCount = 0;
  var gazeFrame = 0;
  var lastPoint = null;
  var pointerOnBibu = false;

  function enabled() {
    return getComputedStyle(document.documentElement).getPropertyValue('--rv-bibu-companion').trim() === 'on';
  }

  function clamp(value) {
    return Math.max(-1, Math.min(1, value));
  }

  function clearBibu() {
    clearTimeout(greetingTimer);
    delete stage.dataset.bibuReaction;
    delete stage.dataset.bibuGreeting;
  }

  function scheduleIdle() {
    clearTimeout(idleTimer);
    if (motion.matches) return;
    idleTimer = setTimeout(function () {
      if (stage && !document.hidden && !stage.querySelector('input:focus') && !stage.querySelector(INVALID)) {
        expressBibu('sleepy', 2400);
      }
    }, 14000);
  }

  function expressBibu(reaction, duration) {
    delete stage.dataset.bibuGaze;
    clearBibu();
    clearTimeout(idleTimer);
    if (reaction === 'greeting') stage.dataset.bibuGreeting = 'true';
    else stage.dataset.bibuReaction = reaction;
    greetingTimer = setTimeout(function () {
      clearBibu();
      scheduleIdle();
    }, duration || 1800);
  }

  function stopGaze() {
    cancelAnimationFrame(gazeFrame);
    gazeFrame = 0;
    if (stage) delete stage.dataset.bibuGaze;
  }

  function cssLength(value, base) {
    if (!value) return 0;
    var number = parseFloat(value) || 0;
    return /%$/.test(value) ? number / 100 * base : number;
  }

  // BiBu is the main content's ::after; its CSS translate/rotate/scale place the visor.
  function artwork() {
    var style = getComputedStyle(stage, '::after');
    var width = parseFloat(style.width);
    var height = parseFloat(style.height);
    if (!(width > 0) || !(height > 0)) return null;
    var box = stage.getBoundingClientRect();
    var left = parseFloat(style.left);
    var top = parseFloat(style.top);
    if (isNaN(left)) left = box.width - parseFloat(style.right) - width;
    if (isNaN(top)) top = box.height - parseFloat(style.bottom) - height;
    var translate = style.translate && style.translate !== 'none' ? style.translate.split(/\s+/) : [];
    var scale = style.scale && style.scale !== 'none' ? style.scale.split(/\s+/).map(Number) : [1];
    var sx = scale[0] || 1;
    var sy = scale.length > 1 ? scale[1] || 1 : sx;
    var angle = (parseFloat(style.rotate) || 0) * Math.PI / 180;
    var cos = Math.cos(angle);
    var sin = Math.sin(angle);
    var centerX = box.left + left + width / 2 + cssLength(translate[0], width);
    var centerY = box.top + top + height / 2 + cssLength(translate[1], height);
    var eyeX = (EYE_X - 0.5) * width * sx;
    var eyeY = (EYE_Y - 0.5) * height * sy;
    return {
      width: width,
      eyeX: centerX + eyeX * cos - eyeY * sin,
      eyeY: centerY + eyeX * sin + eyeY * cos,
      cos: cos,
      sin: sin,
      sx: sx,
      sy: sy,
      mirror: sx * sy < 0 ? -1 : 1,
    };
  }

  function updateGaze() {
    gazeFrame = 0;
    if (!stage || !lastPoint) return;
    var reaction = stage.dataset.bibuReaction;
    var special = stage.dataset.bibuGreeting || (reaction && reaction !== 'curious' && reaction !== 'sleepy');
    if (document.hidden || motion.matches || stage.querySelector(PROTECTED) || special || stage.querySelector(INVALID)) {
      stopGaze();
      return;
    }
    var art = artwork();
    if (!art) {
      stopGaze();
      return;
    }
    if (reaction === 'sleepy') clearBibu();
    var dx = lastPoint.x - art.eyeX;
    var dy = lastPoint.y - art.eyeY;
    var scale = art.width / REFERENCE_WIDTH;
    var reach = Math.max(1, 400 * scale);
    var nearVisor = Math.hypot(dx / Math.max(1, 115 * scale), dy / Math.max(1, 65 * scale)) < 1;
    if (pointerOnBibu) {
      // Resting on BiBu: stay curious instead of following; only the visor delights her.
      if (nearVisor) {
        setGaze(0, 0, 0);
        stage.dataset.bibuGaze = 'delighted';
      } else {
        delete stage.dataset.bibuGaze;
        if (!stage.dataset.bibuReaction && !stage.dataset.bibuGreeting) stage.dataset.bibuReaction = 'curious';
      }
      scheduleIdle();
      return;
    }
    // Pointer offset in BiBu's own axes: undo the rotation, then the scale/mirror.
    var localX = (dx * art.cos + dy * art.sin) / art.sx;
    var localY = (-dx * art.sin + dy * art.cos) / art.sy;
    setGaze(clamp(localX / reach) * 13 * scale, clamp(localY / reach) * 9 * scale, clamp(dx / reach) * 1.5 * art.mirror);
    stage.dataset.bibuGaze = nearVisor ? 'delighted' : 'following';
    scheduleIdle();
  }

  function setGaze(x, y, lean) {
    stage.style.setProperty('--gaze-x', x + 'px');
    stage.style.setProperty('--gaze-y', y + 'px');
    stage.style.setProperty('--gaze-lean', lean + 'deg');
  }

  function onTriggerClick() {
    expressBibu(REACTIONS[greetingCount++ % REACTIONS.length]);
  }

  function onCurious() {
    if (!stage.dataset.bibuReaction && !stage.dataset.bibuGreeting) {
      clearTimeout(idleTimer);
      stopGaze();
      stage.dataset.bibuReaction = 'curious';
    }
  }

  function onLeaveBibu() {
    if (stage.dataset.bibuReaction === 'curious') {
      clearBibu();
      scheduleIdle();
    }
  }

  function onPointerEnterBibu(event) {
    if (event.pointerType === 'mouse') pointerOnBibu = true;
    onCurious();
  }

  function onPointerLeaveBibu(event) {
    if (event.pointerType === 'mouse') pointerOnBibu = false;
    onLeaveBibu();
  }

  function onBlurBibu() {
    if (!pointerOnBibu) onLeaveBibu();
  }

  function onFocusIn(event) {
    if (event.target === trigger) return;
    delete stage.dataset.bibuGaze;
    clearBibu();
    clearTimeout(idleTimer);
  }

  // Typing only clears the current reaction; the value itself is never read.
  function onInput(event) {
    if (event.target instanceof HTMLInputElement && event.target.type !== 'checkbox') {
      clearBibu();
      clearTimeout(idleTimer);
    }
  }

  function onChange(event) {
    if (event.target instanceof HTMLInputElement && event.target.type === 'checkbox' && event.target.checked) {
      expressBibu('love');
    }
  }

  function onPointerMove(event) {
    if (event.pointerType !== 'mouse') return;
    lastPoint = { x: event.clientX, y: event.clientY };
    if (!gazeFrame) gazeFrame = requestAnimationFrame(updateGaze);
  }

  function onVisibility() {
    if (!stage) return;
    delete stage.dataset.bibuGaze;
    clearBibu();
    clearTimeout(idleTimer);
    if (!document.hidden) scheduleIdle();
  }

  function onPageHide() {
    if (!stage) return;
    clearBibu();
    clearTimeout(idleTimer);
    stopGaze();
  }

  function onPageShow() {
    if (stage) scheduleIdle();
  }

  function detach() {
    if (!stage) return;
    clearBibu();
    clearTimeout(idleTimer);
    stopGaze();
    stage.removeEventListener('focusin', onFocusIn);
    stage.removeEventListener('focusout', scheduleIdle);
    stage.removeEventListener('input', onInput);
    stage.removeEventListener('change', onChange);
    stage.removeEventListener('pointermove', onPointerMove);
    stage.removeEventListener('pointerleave', stopGaze);
    if (trigger) trigger.remove();
    stage = null;
    trigger = null;
    pointerOnBibu = false;
  }

  function attach() {
    var next = document.querySelector('.logto_main-content');
    if (next && next === stage) {
      if (!trigger.isConnected) stage.appendChild(trigger);
      return;
    }
    detach();
    if (!next || !enabled()) return;
    stage = next;
    trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'login-bibu-trigger';
    trigger.setAttribute('aria-label', '和 Bibu 打招呼');
    trigger.addEventListener('click', onTriggerClick);
    trigger.addEventListener('pointerenter', onPointerEnterBibu);
    trigger.addEventListener('pointerleave', onPointerLeaveBibu);
    trigger.addEventListener('focus', onCurious);
    trigger.addEventListener('blur', onBlurBibu);
    stage.appendChild(trigger);
    stage.addEventListener('focusin', onFocusIn);
    stage.addEventListener('focusout', scheduleIdle);
    stage.addEventListener('input', onInput);
    stage.addEventListener('change', onChange);
    stage.addEventListener('pointermove', onPointerMove);
    stage.addEventListener('pointerleave', stopGaze);
    scheduleIdle();
  }

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);
  if (motion.addEventListener) motion.addEventListener('change', stopGaze);
  // Logto renders and replaces its layout client-side; keep exactly one companion attached.
  new MutationObserver(attach).observe(document.documentElement, { childList: true, subtree: true });
  attach();
})();
